import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ContentRun, ContentPiece } from "../../../shared/types";

// ── Queries ──

export function useContentRuns() {
  return useQuery({
    queryKey: ["content-runs"],
    queryFn: () => api.get<{ data: ContentRun[] }>("/api/content/runs"),
  });
}

export function useContentRun(runId: string | undefined) {
  return useQuery({
    queryKey: ["content-runs", runId],
    queryFn: () =>
      api.get<{ data: ContentRun }>(`/api/content/runs/${runId}`),
    enabled: !!runId,
    // Poll every 3s while pipeline is processing
    refetchInterval: (query) => {
      const run = query.state.data?.data;
      if (!run) return false;
      const statuses = Object.values(run.agent_status);
      const isProcessing = statuses.some(
        (s) => s === "queued" || s === "working",
      );
      return isProcessing ? 3_000 : false;
    },
  });
}

export function useContentPieces(runId: string | undefined) {
  return useQuery({
    queryKey: ["content-runs", runId, "pieces"],
    queryFn: () =>
      api.get<{ data: ContentPiece[] }>(
        `/api/content/runs/${runId}/pieces`,
      ),
    enabled: !!runId,
  });
}

// ── Mutations ──

export function useStartPipeline() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      brand_id: string;
      checkin_id?: string;
      text?: string;
    }) => api.post<{ data: ContentRun }>("/api/content/run", payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs"] });
    },
  });
}

export function useSelectIdea(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { idea_index: number }) =>
      api.post<{ data: ContentRun }>(
        `/api/content/runs/${runId}/select`,
        payload,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["content-runs", runId] });
      qc.invalidateQueries({ queryKey: ["content-runs"] });
    },
  });
}

export function useUpdatePiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { id: string; data: Partial<ContentPiece> }) =>
      api.put<{ data: ContentPiece }>(
        `/api/content/pieces/${payload.id}`,
        payload.data,
      ),
    onSuccess: (_data, variables) => {
      // Invalidate pieces list for any run that contains this piece
      qc.invalidateQueries({ queryKey: ["content-runs"] });
    },
  });
}
